import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { saveBlobResponse, describeDownloadError } from '@/lib/fileDownload';
import { DialogValidationErrors } from '@/components/forms/DialogValidationErrors';
import { ValidationError } from '@/lib/validationErrors';

interface ExportPlanVersionDialogProps {
  entityId: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Export a plan version to a file, on request and in whatever state it is in.
 *
 * The file is produced and handed straight back — no outbox record and no stored copy. It is the
 * same coded adapter the delivery pipeline uses, so a workbook downloaded here and one delivered
 * to a recipient by an export job are the same document.
 *
 * Delivery to a recipient is a separate thing entirely: an export job listening for the publish
 * event. Pressing this never sends anything out of the system.
 */
export function ExportPlanVersionDialog({ entityId, open, onClose }: ExportPlanVersionDialogProps) {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<ValidationError[]>([]);

  const exportMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.get(`/api/finance/planversion/${entityId}/export-file`, {
        responseType: 'blob',
      });
      // The server names the file — it carries the version number and state. saveBlobResponse
      // falls back on the blob's media type when Content-Disposition is not readable.
      saveBlobResponse(response, 'planversion_export');
    },
    onSuccess: () => {
      setErrors([]);
      onClose();
    },
    onError: async (rawError: unknown) => {
      // A blob request wraps the API's error body in a Blob; describeDownloadError unwraps it, so
      // "this version does not exist" reaches the user instead of a bare status text.
      setErrors([{
        field: 'form',
        message: await describeDownloadError(rawError, 'export'),
        instancePath: '#',
        members: [],
      }]);
    },
  });

  const handleClose = () => {
    setErrors([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('Finance.ExportPlanVersion.Title', { defaultValue: 'Export version' })}
      </DialogTitle>
      <DialogContent>
        <DialogValidationErrors errors={errors} onClear={() => setErrors([])} />

        <DialogContentText sx={{ mb: 2 }}>
          {t('Finance.ExportPlanVersion.Body', {
            defaultValue:
              'Produces an Excel workbook from this version as it stands right now and downloads '
              + 'it. Nothing is sent to a recipient.',
          })}
        </DialogContentText>
        <Alert severity="info">
          {t('Finance.ExportPlanVersion.DraftNotice', {
            defaultValue:
              'A version that is not yet published is a working copy whose figures will still '
              + "change. The file name carries the version's state, so a workbook taken out of the "
              + 'system says which it came from.',
          })}
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
        <Button
          variant="contained"
          onClick={() => exportMutation.mutate()}
          disabled={exportMutation.isPending}
        >
          {exportMutation.isPending
            ? t('Finance.ExportPlanVersion.Preparing', { defaultValue: 'Preparing…' })
            : t('Finance.ExportPlanVersion.Confirm', { defaultValue: 'Export' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
